import { usePostOfficeContextSettled } from '@atlassian/post-office-context/context-settled';
import { usePostOfficeContext } from '@atlassian/post-office-context/post-office-context';
import { usePlacementFetchingContext } from '@atlassian/post-office-placement-data';
import { usePlacementContext } from '@atlassian/post-office-context/placement-context';
import { createUsePlacement, type InitialDataStates } from '@post-office/placement-contexts/hooks';
import type { ErrorState } from '@post-office/placement-contexts/hooks/types';
import { createUsePlacementWithClient } from '@post-office/placement-contexts/hooks/usePlacementWithClient';
import { useSubscriptionConfig } from '@post-office/placement-contexts/create-placement-component/providers/stable-graphql-subscription';
import { type SerializableRecord } from '@post-office/message-template-contracts/serializable-record';
import React, { useMemo } from 'react';

import { type Config } from './types';

export const usePlacementApi = <T extends SerializableRecord>(
	path: string,
	config?: Config,
): InitialDataStates<T> => {
	const { placementId } = usePlacementContext();
	const context = usePostOfficeContext();
	const contextSettled = usePostOfficeContextSettled();
	const fetcher = usePlacementFetchingContext();
	const { subscription } = useSubscriptionConfig();

	const placementApi = React.useMemo(() => {
		if (!placementId) {
			return (): ErrorState => ({
				isLoading: false,
				data: undefined,
				error: new Error(
					'No placementId found. Ensure this hook is used as a child of PlacementProvider.',
				),
			});
		}

		const isClientFetch = config?.useClientFetch ?? false;

		if (isClientFetch) {
			return createUsePlacementWithClient(placementId, fetcher);
		}

		return createUsePlacement(placementId, fetcher);
	}, [placementId, fetcher, config?.useClientFetch]);

	const { queryParams = {} } = config ?? {};
	const placementParams = { ...queryParams, ...context };

	const apiDependencies = useMemo(() => {
		return [config?.autoRefetch?.updateKey, contextSettled];
	}, [config?.autoRefetch?.updateKey, contextSettled]);

	// disable REST fetch for initialMessages for subscription based placements
	const isSubscriptionBasedFetchDisabled = subscription?.enabled === true;
	const isFetchHeld = !contextSettled || isSubscriptionBasedFetchDisabled;

	return placementApi<T, SerializableRecord>(path, placementParams, config?.requestOptions, {
		throttleTime: config?.throttleTime,
		isEnabled: isFetchHeld ? false : config?.autoRefetch?.isEnabled,
		deps: apiDependencies,
	});
};
